import React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useNavigate } from 'react-router-dom'
import { Column } from 'primereact/column'
import { Tag } from 'primereact/tag'
import useModal from '../../../utils/hooks/useModal'
import Modal from '../../../components/modal/Modal'
import FuelStationsOwnAdd from '../../../components/fuel-stations/fuel-stations-own/FuelStationsOwnAdd'
import useAccess from '../../../utils/hooks/useAccess'
import { OwnFuelStationsService } from '../../../services/own-fuel-stations/ownFuelStations.service'
import { ownFuelStation } from '../../../routes/fuel-stations/pathname'
import { GROUPS, MODULES } from '../../../utils/constants/roles'
import useHandlerBasicActions from '../../../utils/hooks/useHandlerBasicActions'
import DataTableComp from '../../../components/common/data-table/DataTableComp'
import MainCommonView from '../../../components/common/MainCommonView'
import rowDelete from '../../../components/common/data-table/column-types/rowDelete'
import rowEditNavigatePath from '../../../components/common/data-table/column-types/rowEditNavigatePath'
import rowViewNavigatePath from '../../../components/common/data-table/column-types/rowViewNavigatePath'
import FulesPriceTable from '../../../components/fuel-stations/fuel-prices/FuelsPriceTable'
import InputTextComp from '../../../components/common/form-components/inputs/InputTextComp'
import InputNumberComp from '../../../components/common/form-components/inputs/InputNumberComp'
import { propsInputNumberCurrency } from '../../../utils/constants/common'
import CheckboxCom from '../../../components/common/form-components/checkbox/CheckboxComp'
import './ownFuelStations.scss'
import AppSection from '../../../component/layout/AppSection.tsx'
import AppTabMenu from '../../../component/layout/AppTabMenu.tsx'
import PriceHistoryDataTable from '../../../components/fuel-stations/fuel-prices/PriceHistoryDataTable'
import ChangePriceForm from '../../../components/fuel-stations/fuel-prices/ChangePriceForm'
import { fuelStationsMenu } from '../../../utils/constants/navlinks-Menu'

const OwnFuelStations = () => {
  const { formatMessage: msg } = useIntl()
  const navigate = useNavigate()

  const { isShowing, toggle } = useModal()
  const { canAdd, canDelete, canEdit, canGet } = useAccess()
  const dataFacade = useHandlerBasicActions({
    getAll: OwnFuelStationsService.getAll,
    sortFieldInitial: 'centerName',
    rowsPerPageInitial: 100,
    createAction: OwnFuelStationsService.create,
    removeAction: OwnFuelStationsService.remove,
  })

  const handleCreate = async (data) => {
    const upData = { ...data, center: data?.center }
    await dataFacade.create(upData)
    toggle()
  }

  const statusBodyTemplate = (fuelStation) => {
    const val = fuelStation?.prices?.auto
      ? msg({ id: 'activated' })
      : msg({ id: 'disabled' })
    return <Tag value={val} severity={getSeverity(fuelStation)} />
  }

  const getSeverity = (fuelStation) => {
    switch (fuelStation?.prices?.auto) {
      case true:
        return 'success'
      case false:
        return 'danger'
      default:
        return null
    }
  }

  const pagerOptions = {
    ...dataFacade?.pagerOptions,
    totalRecords: dataFacade?.data?.total,
  }

  const headerOptions = {
    title: msg({ id: 'owns' }),
    handlerHeaderActions: {
      secondaryButton: canAdd(MODULES.FUELSTATIONS, GROUPS.OWN) && toggle,
      secondaryButtonText: 'button.add',
      secondaryButtonIcon: 'pi-plus',
    },
    searchOptions: dataFacade?.searchOptions,
  }

  const getPricesMinetur = (rowData) => (
    <FulesPriceTable prices={[rowData?.prices?.lastMineturPrices]} />
  )

  const getDataCompetence = (data) => {
    if (data?.favoriteStation) {
      return (
        <div className="own-fuel-compare">
          <InputTextComp value={data?.favoriteStation?.label} disabled />
          <div className="fuel-compare-content">
            <InputNumberComp
              value={data?.difference}
              disabled
              {...propsInputNumberCurrency}
            />
            <CheckboxCom checked={data?.enabled} disabled />
          </div>
        </div>
      )
    }
    return null
  }

  const dataTable = (
    <DataTableComp
      loading={dataFacade?.loading}
      pagerOptions={pagerOptions}
      tableOptions={{
        className: 'table-with-menu ',
        value: dataFacade?.data?.resp,
        size: 'small',
        ...dataFacade?.sortOptions,
      }}
      columns={[
        <Column sortable field="centerName" header={msg({ id: 'center' })} />,
        <Column sortable field="locality" header={msg({ id: 'locality' })} />,
        <Column
          field="minetur"
          header={msg({ id: 'lastMineturPrices' })}
          body={(rowData) => getPricesMinetur(rowData)}
        />,
        <Column
          field="comparisons.comparison1 "
          header={msg({ id: 'competence' }) + ' 1'}
          body={(rowData) =>
            getDataCompetence(rowData?.comparisons?.comparison1)
          }
        />,
        <Column
          field="comparisons.comparison1 "
          header={msg({ id: 'competence' }) + ' 2'}
          body={(rowData) =>
            getDataCompetence(rowData?.comparisons?.comparison2)
          }
        />,
        <Column
          field="comparisons.comparison1 "
          header={msg({ id: 'competence' }) + ' 3'}
          body={(rowData) =>
            getDataCompetence(rowData?.comparisons?.comparison3)
          }
        />,
        <Column
          sortable
          field="prices.auto"
          header={msg({ id: 'update.prices.automatically' })}
          body={statusBodyTemplate}
        />,
        canDelete(MODULES.FUELSTATIONS, GROUPS.OWN) && (
          <Column {...rowDelete({ handleDelete: dataFacade.remove })} />
        ),
        canEdit(MODULES.FUELSTATIONS, GROUPS.OWN) && (
          <Column
            {...rowEditNavigatePath({
              navigateTo: (idRow, state) =>
                navigate(`${ownFuelStation.DATA(idRow)}`, state),
            })}
          />
        ),
        canGet(MODULES.FUELSTATIONS, GROUPS.OWN) && (
          <Column
            {...rowViewNavigatePath({
              navigateTo: (idRow, state) =>
                navigate(`${ownFuelStation.DATA(idRow)}`, state),
            })}
          />
        ),
      ]}
    />
  )

  return (
    <AppSection
      /* refactor this */
      header={<AppTabMenu options={fuelStationsMenu} />}
      headerOptions={headerOptions}
      className="own-fuel-stations"
      dataFacade={dataFacade}
    >
      {dataTable}
      <Modal
        isShowing={isShowing}
        hide={toggle}
        width="100%"
        maxWidth="1200px"
        title={<FormattedMessage id="fuelStation.add" />}
        context={<FuelStationsOwnAdd handleCreate={handleCreate} />}
      />
    </AppSection>
  )
}

export default OwnFuelStations
